/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewRef
} from '@angular/core';
import {SpecialOrderMaterialDetailModel} from '../../api/ms-specialorders/contracts/special-order-material-detail.model';
import {Router} from '@angular/router';
import {MaterialListModalComponent} from '../../spo/spo-create-new/step-material-list/material-list-modal/material-list-modal.component';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {MaterialListItemModel} from './models/material-list-item.model';
import {MaterialListService} from './service/material-list.service';
import {
    combineLatest,
    Subscription
} from 'rxjs';
import {AvailableActions} from '../../api/ms-specialorders/contracts/available-actions.enum';
import {SpecialOrderMaterialStatus} from '../../api/ms-specialorders/contracts/special-order-material-status.enum';
import {TransportDocumentTypeBaseModel} from '../../api/ms-specialorders/contracts/transport-document-type-base.model';
import {MaterialService} from '../../api/ms-specialorders/material/material.service';
import {SpecialOrderPositionValidationResultModel} from '../../api/ms-specialorders/contracts/special-order-position-validation-result.model';
import {WarningsEnum} from '../warnings/warnings.enum';
import {TooltipEnum} from '../tooltip/tooltip.enum';
import {TooltipAlignmentEnum} from '../tooltip/tooltip-alignment.enum';
import {TooltipIconSizeEnum} from '../tooltip/tooltip-icon-size.enum';
import trackByIndex from '../../shared/utils/trackByIndex';
import {ToastWrapperService} from '../utils/toast-wrapper-service/toast-wrapper-service';
import {SpecialOrderMaterialOperationEnum} from '../../api/ms-specialorders/contracts/special-order-material-operation.enum';
import {SpecialOrderMaterialOperationCommandModel} from '../../api/ms-specialorders/contracts/special-order-material-operation-command-model';
import {
    debounceTime,
    startWith
} from 'rxjs/operators';
import {MaterialListMaterialErrorEnum} from './models/material-list-material-error.enum';

@Component({
    selector: 'app-material-list',
    templateUrl: './material-list.component.html',
    styleUrls: ['./material-list.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MaterialListComponent implements OnInit, OnDestroy, OnChanges {
    @Input() transportDocumentType: TransportDocumentTypeBaseModel;
    @Input() spoNumber: string;
    @Input() materials: SpecialOrderMaterialDetailModel[] = [];
    @Input() maxNumberOfMaterials: number;
    @Output() materialToUpdate = new EventEmitter<SpecialOrderMaterialOperationCommandModel>();
    @Output() undoMaterialToUpdate = new EventEmitter<SpecialOrderMaterialDetailModel>();

    /** Material validation **/
    @Input() supplyingPackingPlantCode: string;
    // This variable will update if a new material validation is run
    @Input() invalidMaterials: string[] = [];
    @Input() materialNumbers: string[] = [];
    /** Configuration variables*/
    @Input() showTitle = true;
    @Input() showBtnAdd = true;
    @Input() showCtxMenu = false;
    @Input() maxElements: number;
    @Input() showExtraColumns = false;
    @Input() showTooltipWarnings = false;
    @Input() enableMaterialDetailView = false;
    @Input() refreshMaterialListOnDelete = true;

    @ViewChild('tableRef') tableRef: ElementRef;
    /** Loading configuration variables */
    @Input() enableTableLoading = false;
    public tableIsLoading: boolean;

    public modalIsLoading: boolean;
    /** Specific fields */
    materialsItems: MaterialListItemModel[] = [];
    /** Template variables */
    public contextMenuPadding = 20;
    /** Labels **/
    readonly REQUIRED_FOR_SEND_LABEL = 'Required for send';
    readonly MESSAGE_OVERLAY_MATERIAL_NOT_AVAILABLE = 'This material is not available in the selected packing plant and will be discarded';
    readonly CANCEL_MATERIAL_BUTTON_LABEL = 'Cancel Material';
    readonly MESSAGE_OVERLAY_RUN_OUT_MATERIAL = 'This material is run out and will be discarded';
    /** Tooltips **/
    WarningInlineEnum = WarningsEnum;
    TooltipEnum = TooltipEnum;
    TooltipAlignmentEnum = TooltipAlignmentEnum;
    TooltipIconSizeEnum = TooltipIconSizeEnum;
    /** Variable to store if materials have warnings array  **/
    public hasMaterialWarnings: boolean[] = [];
    public availableActions = AvailableActions;
    public readonly trackByIndex = trackByIndex;
    private positionValidationResult: SpecialOrderPositionValidationResultModel[] = [];
    private subscriptions: Subscription = new Subscription();

    get tableElement(): Element {
        return this.tableRef.nativeElement;
    }

    get colspan(): number {
        return this.showExtraColumns ? 12 : 7;
    }

    get inactiveNumberMaterials(): number {
        return this.materialsItems.filter((item) => item.detail?.material?.active === false).length;
    }

    constructor(private router: Router,
                private modalService: NgbModal,
                private toast: ToastWrapperService,
                private materialListService: MaterialListService,
                private ngZone: NgZone,
                private materialService: MaterialService,
                private changeDetector: ChangeDetectorRef) {
    }

    ngOnInit(): void {
        /** Starts table loading, if enabled */
        this.tableIsLoading = this.enableTableLoading;

        if (this.materials == null) {
            this.materials = [];
        }

        /** Maps the material list **/
        this.mapMaterialItems();
        /** Gets supplying packing plant code **/
        this.refreshSupplyingPackingPlantCode();
        this.changeDetector.detectChanges();
    }

    showMaterialOverlay(material: MaterialListItemModel): boolean {
        return material.overlayInProgress || material.canceled;
    }

    getOverlayErrorMessage(errors: SpecialOrderPositionValidationResultModel, material?: SpecialOrderMaterialDetailModel): string {
        const invalidMaterial = material?.material?.active;
        let errorMessage = null;
        if (invalidMaterial === false || errors?.material?.errors != null) {
            if (invalidMaterial === false
                    || errors.material.errors.find((error) => error.id === MaterialListMaterialErrorEnum.MATERIAL_INACTIVE) != null) {
                errorMessage = this.MESSAGE_OVERLAY_RUN_OUT_MATERIAL;
            }
            if (errors?.material?.errors?.find((error) => error.id === MaterialListMaterialErrorEnum.MATERIAL_NOT_FOUND) != null) {
                errorMessage = this.MESSAGE_OVERLAY_MATERIAL_NOT_AVAILABLE;
            }
        }
        return errorMessage;
    }

    /**
     * Gets the new supplying packing plant code from service
     **/
    refreshSupplyingPackingPlantCode(): void {
        combineLatest([
            this.materialListService.invalidMaterials.pipe(startWith({materialNumbers: []})),
            this.materialListService.materialsErrors.pipe(startWith([])),
            this.materialListService.supplyingPackingPlantCode
        ])
                .pipe(debounceTime(0))
                .subscribe(([
                                invalidMaterialsValue,
                                materialsErrorsValue,
                                plantCode
                            ]) => {
                    this.invalidMaterials = invalidMaterialsValue.materialNumbers;
                    this.positionValidationResult = materialsErrorsValue;
                    this.supplyingPackingPlantCode = plantCode;

                    this.updateMaterialListHasErrors();
                    this.mapMaterialItems();

                    if (!(this.changeDetector as ViewRef).destroyed) {
                        this.changeDetector.detectChanges();
                    }
                });
    }

    /**
     * Maps materials from {@link SpecialOrderMaterialDetailModel} to {@link MaterialListItemModel}
     * Updates the {@link materialNumbers} and the {@link MaterialListService}
     * **/
    mapMaterialItems(): void {
        this.materialsItems = this.materials?.map((material) => {
            /** Create the materials codes array **/
            this.materialNumbers.push(material.material.number);
            this.materialListService.refreshMaterialNumbers(this.materialNumbers);
            const validationReport = this.positionValidationResult.find((item) => material.position === item.row);

            let materialModel: MaterialListItemModel = {
                detail: material,
                overlayInProgress: false,
                canceled: false,
                isOpen: false,
                invalid: false
            };

            if (material.status === undefined || material.status.code === undefined || material.status.code === 'DRAFT') {
                materialModel = {
                    ...materialModel,
                    errors: validationReport,
                    errorMessage: this.getOverlayErrorMessage(validationReport, material),
                    invalid: this.invalidMaterials.includes(material.material.number)
                };
            }

            /** Return the new material object **/
            return materialModel;
        }) ?? [];
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['materials'] && !changes['materials'].firstChange) {
            this.mapMaterialItems();
            this.tableIsLoading = false;
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    /** Creates the view more link path*/
    viewMore(): void {
        this.router.navigate(['spo', this.spoNumber]);
    }

    /** Determines if the view more link should be displayed */
    showViewMore(): boolean {
        return this.spoNumber && this.materialsItems && this.materialsItems.length > this.maxElements;
    }

    /** Determines if the overlay over the last list element should be displayed (view more) **/
    showViewMoreOverlay(rowIndex: number): boolean {
        return rowIndex === this.maxElements - 1 && this.showViewMore();
    }

    /** Adds a material to the material list */
    add(): void {
        const modalRef = this.modalService.open(MaterialListModalComponent, {centered: true});
        modalRef.componentInstance.supplyingPackingPlantCode = this.supplyingPackingPlantCode;
        modalRef.componentInstance.materialsLength = this.materialsItems.length - this.inactiveNumberMaterials;
        modalRef.componentInstance.maxNumberOfMaterials = this.maxNumberOfMaterials;

        this.subscriptions.add(
                modalRef.componentInstance.addClick.subscribe(
                        (material: SpecialOrderMaterialDetailModel) => {
                            this.addMaterialItems([material]);
                            modalRef.componentInstance.materialsLength = this.materialsItems.length - this.inactiveNumberMaterials;
                            this.toast.success(
                                    `Material No. <strong>${material.material.number}</strong> added successfully`,
                                    'Success');
                        }
                )
        );
    }

    addExcelMaterials(materials: SpecialOrderPositionValidationResultModel[]): void {
        const materialDetails: SpecialOrderMaterialDetailModel[] = materials.map((material) => ({
            position: undefined,
            status: undefined,
            availableActions: undefined,
            material: material.material.value,
            modelType: material.modelType.value,
            lotNumber: material.lotNumber.value,
            mcdrNumber: material.mcdrNumber.value,
            igefNumber: material.igefNumber.value,
            colourCode: material.colourCode.value,
            orderedQuantity: Number(material.orderQuantity.value),
            alert: undefined
        }));

        this.addMaterialItems(materialDetails);

        this.toast.success(
                `${materialDetails.length} materials <strong>added</strong> successfully`,
                'Success');
    }

    addMaterialItems(materialItems: SpecialOrderMaterialDetailModel[]): void {
        if (materialItems == null || materialItems.length === 0) {
            return;
        }
        if (this.materialsItems == null) {
            this.materialsItems = [];
        }
        materialItems.forEach((material) => {
            /** Refresh the materials details array **/
            const element: MaterialListItemModel = {
                detail: material,
                overlayInProgress: false,
                canceled: false,
                invalid: this.invalidMaterials.includes(material.material.number),
                isOpen: false
            };
            // Adds if material was material warnings to array
            this.hasMaterialWarnings.push(this.showMaterialWarningTooltip(material));
            // For new materials set position at last to be identified on delete/cancel operations
            if (this.materialsItems.length > 0) {
                // Extracts the highest position on the list and increments it so that this becomes the last position
                element.detail.position = this.materialsItems
                        .reduce((previous, current) => previous.detail.position > current.detail.position ? previous : current)
                        .detail.position + 1;
            } else {
                // If there are no materials, set position as 1 (positions now start at 1 instead of 0)
                element.detail.position = 1;
            }
            // Add to list
            this.materialsItems.push(element);
            this.materialNumbers.push(material.material.number);
        });
        // Updated material service data
        this.materialListService.refreshMaterialListHasWarnings(this.hasMaterialWarnings.includes(true));
        this.materialListService.refreshListItemData(this.materialsItems);
        this.materialListService.refreshMaterialNumbers(this.materialNumbers);
    }

    /**
     * Sets new modal material
     * Calls {@link openEditModal} with material
     * @param material the material to edit
     */
    edit(material: MaterialListItemModel): void {
        let modalMaterial: SpecialOrderMaterialDetailModel = material.detail;
        if (this.supplyingPackingPlantCode !== modalMaterial.material.supplyingPackingPlantCode) {
            this.modalIsLoading = true;
            this.materialService.getAutocompleteMaterialsList(modalMaterial.material.number, this.supplyingPackingPlantCode, 0, 20)
                    .subscribe((newMaterial) => {
                        modalMaterial = Object.assign({}, modalMaterial, {material: newMaterial[0]});
                        this.openEditModal(material);
                    }, () => {
                        this.toast.error('Something went wrong. Please try again', 'Error');
                    })
                    .add(() => {
                        this.modalIsLoading = false;
                    });
        } else {
            this.openEditModal(material);
        }
    }

    /**
     * Opens material modal in edit mode
     * @param material The material to edit
     * @param material The material in the material list
     */
    openEditModal(material: MaterialListItemModel): void {
        const modalRef = this.modalService.open(MaterialListModalComponent, {centered: true});
        const modalInstance: MaterialListModalComponent = modalRef.componentInstance;
        modalInstance.selectedMaterial = material.detail;
        modalRef.componentInstance.supplyingPackingPlantCode = this.supplyingPackingPlantCode;
        modalRef.componentInstance.addClick.subscribe(
                (editedMaterial: SpecialOrderMaterialDetailModel) => {
                    this.materialsItems[this.materialsItems.indexOf(material)] = {
                        detail: editedMaterial,
                        overlayInProgress: false,
                        canceled: false,
                        invalid: this.invalidMaterials.includes(editedMaterial.material.number),
                        isOpen: false
                    };
                    // After the successful material edition:
                    // 1. Update the material list data
                    this.materialListService.refreshListItemData(this.materialsItems);
                    // 2. Update the position validation result
                    this.updateSpecialOrderPositionValidationResult();
                    // 3. Update the material list has errors flag
                    this.updateMaterialListHasErrors();
                    this.toast.success(
                            `Material No. <strong>${editedMaterial.material.number}</strong> updated successfully`,
                            'Update success');
                }
        );
    }

    /**
     * Cancels a material from the material list
     * @param material The material to cancel
     * */
    cancelMaterial(material: MaterialListItemModel): void {
        this.materialToUpdate.emit({
            position: material.detail.position,
            action: SpecialOrderMaterialOperationEnum.CANCEL
        });
        material.canceled = true;
        this.cancelOverlayInProgress(material);
    }

    /**
     * Undo the cancel material from the material list
     * @param material The material to undo the cancel
     * */
    undoCancelMaterial(material: MaterialListItemModel): void {
        // FIXME: ngZone.run
        this.ngZone.run(() => {
            this.undoMaterialToUpdate.emit(material.detail);
            material.canceled = false;
        });
    }

    /**
     * Deletes a material from the material list
     * @param index The index of the material to delete
     * */
    delete(index: number): void {
        const [deletedMaterial]: MaterialListItemModel[] = this.materialsItems.splice(index, 1);
        this.materialNumbers = this.materialNumbers.filter((code) => code !== deletedMaterial.detail.material.number);
        this.materialToUpdate.emit({
            position: deletedMaterial.detail.position,
            action: SpecialOrderMaterialOperationEnum.DELETE
        });
        if (this.refreshMaterialListOnDelete) {
            this.materialListService.refreshListItemData(this.materialsItems);
        }
        this.toast.success(
                `Material No. <strong>${deletedMaterial.detail.material.number}</strong> deleted successfully`,
                'Success');
    }

    /**
     * Sets a material overlay to true
     * @param material The material to apply the overlay
     * */
    overlayInProgress(material: MaterialListItemModel): void {
        material.overlayInProgress = true;
    }

    /**
     * Removes a material overlay
     * @param material The material to remove the overlay
     * */
    cancelOverlayInProgress(material: MaterialListItemModel): void {
        material.overlayInProgress = false;
    }

    /**
     * check if the special Order Material has conditions to allow actions.
     * @param detail the detail of the special order material
     * @return if the material has conditions to process actions returns true. If not returns false.
     */
    isAllowed(detail: SpecialOrderMaterialDetailModel): boolean {
        const showCreate: boolean = !detail.availableActions && !detail.status;
        const showEdit: boolean = !showCreate && (detail.status && detail.status.code !== SpecialOrderMaterialStatus.CANCELLED &&
                detail.status && detail.status.code !== SpecialOrderMaterialStatus.PACKAGING &&
                detail.availableActions && detail.availableActions.filter((a) => AvailableActions[a] != null).length > 0);
        const blockEdit: boolean = detail.status ? detail.status.code !== SpecialOrderMaterialStatus.PACKAGING : true;
        return blockEdit && (showCreate || showEdit);
    }

    canEdit(detail: SpecialOrderMaterialDetailModel): boolean {
        return detail.availableActions === undefined
                || detail.availableActions.includes(AvailableActions.EDIT);
    }

    canDelete(detail: SpecialOrderMaterialDetailModel): boolean {
        return (detail.availableActions === undefined
                        || detail.availableActions.includes(AvailableActions.DELETE)
                        || detail.availableActions.includes(AvailableActions.EDIT_POSITION_DELETE))
                && detail.material.active;
    }

    canCancel(item: MaterialListItemModel): boolean {
        return item.detail.availableActions
                && item.detail.availableActions.includes(AvailableActions.EDIT_POSITION_CANCEL) && !item.canceled;
    }

    /**
     * Opens and closes the material detail accordion
     * @param listItem The list item to open the accordion for
     * **/
    accordionChangeState(listItem: MaterialListItemModel): void {
        listItem.isOpen = !listItem.isOpen;
    }

    /**
     * Enabled or disabled the open material detail arrow button
     * @param listItem The list item to enable or disable the button for
     */
    toggleMaterialDetailButton(listItem: MaterialListItemModel): boolean {
        if (listItem.detail.deliveries) {
            const sentQuantities = listItem.detail.deliveries.filter((delivery) => delivery.sentQuantity > 0);
            return sentQuantities.length > 0;
        } else {
            return false;
        }
    }

    /**
     * Gets a material order quantity tooltip error text from the position validation result errors descriptions
     * @param material the material
     * @return string[] all the material's order quantity errors descriptions
     */
    getOrderQuantityErrorTooltip(material: MaterialListItemModel): string[] {
        const warningsArray: string[] = [];
        if (material.errors != null && material.errors.orderQuantity != null && material.errors.orderQuantity.errors != null) {
            material.errors.orderQuantity.errors.forEach((error) => warningsArray.push(error.description));
        }
        return warningsArray;
    }

    /**
     * Checks if a material error tooltip should be disabled
     * @param material the material
     * @return boolean if the material error tooltip should be disabled
     */
    disableOrderQuantityErrorTooltip(material: MaterialListItemModel): boolean {
        if (material.errors != null && material.errors.orderQuantity != null) {
            return material.errors.orderQuantity.valid;
        }
        return true;
    }

    /**
     * Determines if a material in list should show material warning tooltip
     * @returns boolean
     * **/
    showMaterialWarningTooltip(material: SpecialOrderMaterialDetailModel): boolean {
        return !!material.material.shelfLife
                || !material.material.latestVersion
                || (material.material.latestVersion && material.material.version !== material.material.latestVersion);
    }

    getMaterialWarningTooltipLabel(material: SpecialOrderMaterialDetailModel): string[] {
        const warningLabels = [];
        if (!material.material.latestVersion) {
            warningLabels.push(`Material not yet sent from ${this.supplyingPackingPlantCode}.`);
        }
        if (material.material.latestVersion && material.material.version !== material.material.latestVersion) {
            const materialNumber = material.material.number.split('-')[0];
            warningLabels.push(`Last shipped material was ${materialNumber}-${material.material.latestVersion}.`);
        }
        if (!!material.material.shelfLife) {
            warningLabels.push('Shelf life might be less than '
                    + material.material.shelfLife + ' ' + material.material.shelfLifePeriodType.name
                    + '. Please consider appropriate transport mode.');
        }
        return warningLabels;
    }

    undoAll(): any {
        this.materialsItems.forEach((child) => child.overlayInProgress = false);
    }

    /**
     * Creates the material alert tooltip text
     * @param material The material to which the tooltip text must be created
     * @return string [] | string A string or an array of strings for the tooltip message
     */
    getMaterialAlertsTooltip(material: SpecialOrderMaterialDetailModel): string[] | string {
        if (material.alerts != null && material.alerts.length > 0) {
            if (material.alerts.length > 1) {
                const alerts: string[] = [];
                material.alerts.forEach((alert) => alerts.push(alert.message));
                return alerts;
            }
            return material.alerts[0].message;
        }
        return null;
    }

    /**
     * Gets the material alerts table cell text
     * @param material The material to which the text must be created
     * @return string The cell text
     */
    getMaterialAlertsText(material: SpecialOrderMaterialDetailModel): string {
        if (material.alerts != null && material.alerts.length > 0) {
            if (material.alerts.length > 1) {
                const number = `(${material.alerts.length})`;
                const messages = material.alerts.map((alert) => alert.message);
                return `${number} ${messages.join('; ')}`;
            }
            return material.alerts[0].message;
        }
        return '-';
    }

    /**
     * Determines if a material has alerts
     * @param material The special order material
     * @return boolean If the materia has alerts
     */
    hasMaterialAlerts(material: SpecialOrderMaterialDetailModel): boolean {
        return material.alerts != null && material.alerts.length > 0;
    }

    public hasMoreMaterialsThanNumberOfMaterialsConfig(): boolean {
        const numberOfMaterialsConfig = this.maxNumberOfMaterials;
        return this.materialsItems.length >= numberOfMaterialsConfig;
    }

    /**
     * Calls the {@link updateSpecialOrderPositionValidationResult} to update the validation result variable and
     * update the material list service flag that checks if there are any errors on the material list
     */
    private updateMaterialListHasErrors(): void {
        this.materialListService.refreshMaterialListHasErrors(
                this.positionValidationResult.some((entry) => !entry.valid && entry.material != null && entry.material.valid)
        );
    }

    /**
     * Updates the material list new {@link SpecialOrderPositionValidationResultModel}, updates the {@link positionValidationResult}
     * and calls the {@link updateMaterialListHasErrors}
     */
    private updateSpecialOrderPositionValidationResult(): void {
        const report: SpecialOrderPositionValidationResultModel[] = [];
        this.materialsItems.forEach((mat) => {
            if (mat.errors != null) {
                report.push(mat.errors);
            }
        });
        this.positionValidationResult = report;
        this.materialListService.refreshMaterialsErrors(this.positionValidationResult);
    }
}
